import React, { useState } from 'react'
import {
  BarChart3,
  Calendar,
  Download,
  Filter,
  TrendingUp,
  Users,
  Euro,
  FileText,
  Printer,
  Search
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts'

import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import Modal from '../components/Modal'

import relatoriosService, {
  RelatorioFilters,
  formatarValor,
  formatarNumero,
  formatarData,
  tiposRelatorio,
  periodosRelatorio,
  coresGrafico
} from '../services/relatorios'

interface DateRange {
  inicio: string
  fim: string
}

const RelatoriosPage: React.FC = () => {
  const [filtros, setFiltros] = useState({
    periodo: '30',
    tipoRelatorio: 'vendas',
    dataInicio: '',
    dataFim: ''
  })

  const [showFiltrosModal, setShowFiltrosModal] = useState(false)

  // Calcular datas baseado no período
  const getDateRange = (): RelatorioFilters => {
    const hoje = new Date()
    const dataFim = hoje.toISOString().split('T')[0]
    
    let dataInicio: string
    if (filtros.periodo === 'personalizado') {
      dataInicio = filtros.dataInicio
    } else {
      const diasAtras = parseInt(filtros.periodo)
      const inicio = new Date(hoje.getTime() - diasAtras * 24 * 60 * 60 * 1000)
      dataInicio = inicio.toISOString().split('T')[0]
    }

    return { dataInicio, dataFim }
  }

  // Queries para dados dos relatórios
  const { data: relatorioVendas, isLoading: isLoadingVendas } = useQuery({
    queryKey: ['relatorio', 'vendas', filtros],
    queryFn: () => relatoriosService.getRelatorioVendas(getDateRange()),
    enabled: filtros.tipoRelatorio === 'vendas'
  })

  const { data: relatorioOS, isLoading: isLoadingOS } = useQuery({
    queryKey: ['relatorio', 'ordens-servico', filtros],
    queryFn: () => relatoriosService.getRelatorioOrdensServico(getDateRange()),
    enabled: filtros.tipoRelatorio === 'ordens'
  })

  const { data: relatorioEstoque, isLoading: isLoadingEstoque } = useQuery({
    queryKey: ['relatorio', 'estoque', filtros],
    queryFn: () => relatoriosService.getRelatorioEstoque(),
    enabled: filtros.tipoRelatorio === 'produtos'
  })

  const { data: relatorioClientes, isLoading: isLoadingClientes } = useQuery({
    queryKey: ['relatorio', 'clientes', filtros],
    queryFn: () => relatoriosService.getRelatorioClientes(getDateRange()),
    enabled: filtros.tipoRelatorio === 'clientes'
  })

  const { data: dashboardData } = useQuery({
    queryKey: ['dashboard', 'resumo', filtros.periodo],
    queryFn: () => relatoriosService.getDashboardData(filtros.periodo)
  })

  const handleExportPDF = async () => {
    try {
      const tipoMap: Record<string, 'vendas' | 'ordens-servico' | 'estoque' | 'clientes'> = {
        vendas: 'vendas',
        ordens: 'ordens-servico',
        produtos: 'estoque',
        clientes: 'clientes'
      }
      
      await relatoriosService.exportarPDF(tipoMap[filtros.tipoRelatorio], getDateRange())
    } catch (error) {
      console.error('Erro ao exportar PDF:', error)
    }
  }

  const handleExportExcel = () => {
    // Implementar exportação em Excel se necessário
    console.log('Exportar relatório em Excel')
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Relatórios</h1>
          <p className="text-gray-600 mt-1">
            Análise detalhada do desempenho da sua oficina
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowFiltrosModal(true)}
            leftIcon={<Filter className="h-4 w-4" />}
          >
            Filtros
          </Button>
          <Button
            variant="outline"
            onClick={handleExportExcel}
            leftIcon={<Download className="h-4 w-4" />}
          >
            Excel
          </Button>
          <Button
            variant="outline"
            onClick={handleExportPDF}
            leftIcon={<FileText className="h-4 w-4" />}
          >
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={handlePrint}
            leftIcon={<Printer className="h-4 w-4" />}
          >
            Imprimir
          </Button>
        </div>
      </div>

      {/* Resumo Executivo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Faturamento Total</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatarValor(dashboardData?.resumo.receita.total || 0)}
              </p>
              <p className="text-xs text-green-600 mt-1">Este mês</p>
            </div>
            <div className="p-3 rounded-lg bg-green-100">
              <Euro className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total de Vendas</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatarNumero(dashboardData?.resumo.vendas.total || 0)}
              </p>
              <p className="text-xs text-blue-600 mt-1">Este mês</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-100">
              <BarChart3 className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Ordens de Serviço</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatarNumero(dashboardData?.resumo.ordensServico.total || 0)}
              </p>
              <p className="text-xs text-purple-600 mt-1">Este mês</p>
            </div>
            <div className="p-3 rounded-lg bg-purple-100">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Novos Clientes</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatarNumero(dashboardData?.resumo.clientes.novos || 0)}
              </p>
              <p className="text-xs text-orange-600 mt-1">Este mês</p>
            </div>
            <div className="p-3 rounded-lg bg-orange-100">
              <Users className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Relatório de Vendas */}
      {filtros.tipoRelatorio === 'vendas' && (
        <div className="space-y-8">
          {/* Gráfico de Evolução das Vendas */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Evolução das Vendas</h2>
              <p className="text-sm text-gray-600">Acompanhe o desempenho das vendas ao longo do tempo</p>
            </div>
            <div className="p-6">
              {isLoadingVendas ? (
                <div className="h-80 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={relatorioVendas?.vendasPorDia || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="data" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip 
                      formatter={(value, name) => [
                        name === 'receita' ? formatarValor(Number(value)) : formatarNumero(Number(value)),
                        name === 'receita' ? 'Faturamento' : 'Quantidade de Vendas'
                      ]}
                      labelFormatter={(label) => formatarData(label)}
                    />
                    <Legend />
                    <Bar 
                      yAxisId="left"
                      dataKey="vendas" 
                      fill={coresGrafico.primary} 
                      name="Vendas"
                    />
                    <Bar 
                      yAxisId="right"
                      dataKey="receita" 
                      fill={coresGrafico.secondary} 
                      name="Faturamento"
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Top Produtos e Formas de Pagamento */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Top Produtos */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Produtos Mais Vendidos</h2>
                <p className="text-sm text-gray-600">Rankings dos produtos por quantidade e faturamento</p>
              </div>
              <div className="p-6">
                {isLoadingVendas ? (
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {relatorioVendas?.produtosMaisVendidos.slice(0, 5).map((produto, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-blue-600">{index + 1}</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{produto.produto.nome}</p>
                            <p className="text-sm text-gray-500">SKU: {produto.produto.codigoInterno}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{formatarNumero(produto._sum.quantidade)} un</p>
                          <p className="text-sm text-green-600">{formatarValor(produto._sum.total)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Formas de Pagamento */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Formas de Pagamento</h2>
                <p className="text-sm text-gray-600">Distribuição por método de pagamento</p>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={relatorioVendas?.formasPagamentoMaisUsadas.map((item, index) => ({
                        name: item.formaPagamento,
                        value: item._sum.valor,
                        quantidade: item._count.id,
                        fill: Object.values(coresGrafico)[index % Object.values(coresGrafico).length]
                      }))}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${formatarValor(value)}`}
                    >
                      {relatorioVendas?.formasPagamentoMaisUsadas?.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={Object.values(coresGrafico)[index % Object.values(coresGrafico).length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatarValor(Number(value))} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Filtros */}
      <Modal
        isOpen={showFiltrosModal}
        onClose={() => setShowFiltrosModal(false)}
        title="Filtros do Relatório"
        size="md"
      >
        <div className="space-y-4">
          <Select
            label="Tipo de Relatório"
            value={filtros.tipoRelatorio}
            onChange={(e) => setFiltros(prev => ({ ...prev, tipoRelatorio: e.target.value }))}
            options={[
              { value: 'vendas', label: 'Relatório de Vendas' },
              { value: 'ordens', label: 'Relatório de Ordens de Serviço' },
              { value: 'produtos', label: 'Relatório de Produtos' },
              { value: 'clientes', label: 'Relatório de Clientes' }
            ]}
          />

          <Select
            label="Período"
            value={filtros.periodo}
            onChange={(e) => setFiltros(prev => ({ ...prev, periodo: e.target.value }))}
            options={[
              { value: '7', label: 'Últimos 7 dias' },
              { value: '30', label: 'Últimos 30 dias' },
              { value: '90', label: 'Últimos 3 meses' },
              { value: '365', label: 'Último ano' },
              { value: 'personalizado', label: 'Período personalizado' }
            ]}
          />

          {filtros.periodo === 'personalizado' && (
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="date"
                label="Data Início"
                value={filtros.dataInicio}
                onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
              />
              <Input
                type="date"
                label="Data Fim"
                value={filtros.dataFim}
                onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowFiltrosModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                // Aplicar filtros
                setShowFiltrosModal(false)
              }}
            >
              Aplicar Filtros
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default RelatoriosPage